import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { DCDesignSample } from "./types";

const gqlEndpoint = `${environment.baseUrl}/graphql`;

type Coordinates = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type GenerateSampleParams = {
  designId: number;
  designName: string;
  artTypeSymbol: "R" | "M" | "X";
  repeatWidthIn: number;
  coordinates: Coordinates;
  wallpaperScaleIn?: number | null;
  pat19Url: string;
  collectionImageUrl: string;
};

const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
  const { data } = await axios.post(gqlEndpoint, { query, variables }, {
    headers: { "X-Wallmates-Auth": authToken }
  });
  if (data.errors) throw new Error(data.errors[0]?.message || "GraphQL error");
  return data.data;
};

export const useDesignSamples = () => {
  const [, setSnackbar] = useSnackbar();
  const [, authToken, loginFunctions] = useLogin();
  const [samples, setSamples] = useState<DCDesignSample[]>([]);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectingSample, setSelectingSample] = useState(false);
  const pollingTimeoutRef = useRef<number | null>(null);
  const pollingDesignIdRef = useRef<number | null>(null);
  const pollingInFlightRef = useRef(false);
  const pollingActiveRef = useRef(false);
  const pollingSkipDesignIdRef = useRef<number | null>(null);

  const fetchSamples = useCallback(async (designId: number, silent = false) => {
    if (!designId) return null;
    try {
      if (!silent) setLoadingSamples(true);
      const q = `query($design_id:ID!){ dcDesignSamples(design_id:$design_id){ id design_id status coordinates wallpaper_scale_in sample_primary_path sample_rotated_path sample_listing_image is_selected created_at updated_at } }`;
      const data = await graphqlRequest(authToken, q, { design_id: designId });
      const next = (data?.dcDesignSamples || []) as DCDesignSample[];
      setSamples(next);
      return next;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Failed to load samples." });
      return null;
    } finally {
      if (!silent) setLoadingSamples(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      window.clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    pollingDesignIdRef.current = null;
    pollingInFlightRef.current = false;
    pollingActiveRef.current = false;
  }, []);

  const pollOnce = useCallback(async (designId: number) => {
    if (!designId || pollingInFlightRef.current) return;
    pollingInFlightRef.current = true;
    try {
      const next = await fetchSamples(designId, true);
      if (!next?.length) {
        pollingSkipDesignIdRef.current = designId;
        stopPolling();
        return;
      }
      const shouldContinue = next.some((sample) => {
        const status = sample.status ?? "";
        return status === "pending" || status === "generating";
      });
      if (!shouldContinue) {
        pollingSkipDesignIdRef.current = designId;
        stopPolling();
        return;
      }
      pollingTimeoutRef.current = window.setTimeout(() => {
        if (pollingActiveRef.current && pollingDesignIdRef.current === designId) {
          pollOnce(designId);
        }
      }, 2000);
    } finally {
      pollingInFlightRef.current = false;
    }
  }, [fetchSamples, stopPolling]);

  const startPolling = useCallback((designId: number, options?: { force?: boolean }) => {
    if (!designId) return;
    if (!options?.force && pollingSkipDesignIdRef.current === designId) return;
    if (pollingActiveRef.current && pollingDesignIdRef.current === designId) return;
    stopPolling();
    pollingActiveRef.current = true;
    pollingDesignIdRef.current = designId;
    pollOnce(designId);
  }, [pollOnce, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const listSamples = useCallback(async (designId: number, options?: { force?: boolean }) => {
    if (options?.force) {
      pollingSkipDesignIdRef.current = null;
    }
    const next = await fetchSamples(designId, false);
    if (!next?.length) {
      pollingSkipDesignIdRef.current = designId;
      stopPolling();
      return next;
    }
    const shouldContinue = next.some((sample) => {
      const status = sample.status ?? "";
      return status === "pending" || status === "generating";
    });
    if (shouldContinue) {
      startPolling(designId);
    } else {
      pollingSkipDesignIdRef.current = designId;
      stopPolling();
    }
    return next;
  }, [fetchSamples, startPolling, stopPolling]);

  const generateSample = useCallback(async (params: GenerateSampleParams) => {
    const {
      designId,
      designName,
      artTypeSymbol,
      repeatWidthIn,
      coordinates,
      wallpaperScaleIn,
      pat19Url,
      collectionImageUrl
    } = params;

    if (!designId) return null;
    if (samples.filter((s) => s.status !== "failed").length >= 5) {
      setSnackbar({ show: true, snackbarLevel: "error", text: "Sample limit reached. Delete a sample to generate a new one." });
      return null;
    }
    if (!pat19Url || !collectionImageUrl) {
      setSnackbar({ show: true, snackbarLevel: "error", text: "Design files are missing for sample generation." });
      return null;
    }
    if (!repeatWidthIn || repeatWidthIn <= 0) {
      setSnackbar({ show: true, snackbarLevel: "error", text: "Repeat width must be greater than 0." });
      return null;
    }

    let sampleId: number | null = null;
    try {
      setGenerating(true);
      setError(null);

      const generateMutation = `mutation($input:DCDesignSampleGenerateInput!){ generateDCDesignSample(input:$input){ id design_id status coordinates wallpaper_scale_in sample_primary_path sample_rotated_path sample_listing_image is_selected created_at updated_at } }`;
      const created = await graphqlRequest(authToken, generateMutation, {
        input: {
          design_id: designId,
          design_name: designName,
          art_type_symbol: artTypeSymbol,
          repeat_width_in: repeatWidthIn,
          coordinates,
          wallpaper_scale_in: wallpaperScaleIn ?? null,
          pat_19_url: pat19Url,
          collection_image_url: collectionImageUrl
        }
      });
      const createdSample = created?.generateDCDesignSample as DCDesignSample | undefined;
      if (!createdSample?.id) {
        throw new Error("Failed to create sample record.");
      }
      sampleId = Number(createdSample.id);
      pollingSkipDesignIdRef.current = null;
      startPolling(designId, { force: true });
      setSamples((prev) => [createdSample, ...prev.filter((sample) => sample.id !== createdSample.id)]);
      return createdSample || null;
    } catch (err: any) {
      setError(err?.message || "Sample generation failed.");
      if (sampleId) {
        try {
          const updateMutation = `mutation($id:ID!,$input:DCDesignSampleUpdateInput!){ updateDCDesignSample(id:$id,input:$input){ id } }`;
          await graphqlRequest(authToken, updateMutation, {
            id: sampleId,
            input: { status: "failed" }
          });
        } catch (updateErr: any) {
          errorCatcher(updateErr, loginFunctions.logout);
        }
      }
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Sample generation failed." });
      return null;
    } finally {
      setGenerating(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  const setSelectedSample = useCallback(async (sampleId: number, designId: number) => {
    if (!sampleId || !designId) return null;
    try {
      setSelectingSample(true);
      const mutation = `mutation($id:ID!,$design_id:ID!){ setDCDesignSampleSelected(id:$id,design_id:$design_id){ id design_id status coordinates wallpaper_scale_in sample_primary_path sample_rotated_path sample_listing_image is_selected created_at updated_at } }`;
      const data = await graphqlRequest(authToken, mutation, { id: sampleId, design_id: designId });
      const updated = data?.setDCDesignSampleSelected as DCDesignSample | undefined;
      if (updated) {
        setSamples((prev) =>
          prev.map((sample) => ({
            ...sample,
            is_selected: sample.id === updated.id
          }))
        );
      }
      return updated || null;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Failed to update sample selection." });
      return null;
    } finally {
      setSelectingSample(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  const deleteSample = useCallback(async (sampleId: number, designId: number) => {
    if (!sampleId || !designId) return null;
    try {
      setSelectingSample(true);
      const mutation = `mutation($id:ID!,$design_id:ID!){ deleteDCDesignSample(id:$id,design_id:$design_id){ id design_id status coordinates wallpaper_scale_in sample_primary_path sample_rotated_path sample_listing_image is_selected created_at updated_at } }`;
      const data = await graphqlRequest(authToken, mutation, { id: sampleId, design_id: designId });
      const selected = data?.deleteDCDesignSample as DCDesignSample | null | undefined;
      const next = await listSamples(designId, { force: true });
      return {
        samples: next || [],
        selected: selected || null
      };
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Failed to delete sample." });
      return null;
    } finally {
      setSelectingSample(false);
    }
  }, [authToken, listSamples, loginFunctions.logout, setSnackbar]);

  const latestSample = useMemo(() => {
    if (!samples.length) return null;
    return [...samples]
      .filter((sample) => Boolean(sample.sample_primary_path))
      .sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bTime - aTime;
      })[0] || null;
  }, [samples]);

  const atSampleLimit = samples.filter((s) => s.status !== "failed").length >= 5;

  return {
    samples,
    latestSample,
    loadingSamples,
    generating,
    selectingSample,
    atSampleLimit,
    error,
    listSamples,
    startPolling,
    stopPolling,
    generateSample,
    setSelectedSample,
    deleteSample
  };
};
